// service
import { endOfDay, format, isValid, startOfDay } from "date-fns";

// utils
import {
	IComparator,
	IQueryField,
	QueryValue,
	RelativeDateValue,
	getRelativeDateLabel,
	unRegexQueryValue,
} from "../../../../../components/QueryBuilder/QueryBuilderUtils";
import { IQuery } from "../../../../../components/QueryBuilder/QueryBuilderUtils";
// constants
import {
	DATE_TYPES,
	DROPDOWN_SELECTION_TYPES,
	GROUP_TYPE,
	NOT_REGEX_COMPARATOR_VALUES,
	NUMERIC_QUERY_OPTIONS,
	NUMERIC_TYPES,
	PEOPLE_SELECTION_TYPES,
	REGEX_COMPARATOR_VALUES,
	RELATIVE_DATE_QUERY_OPTIONS,
	STATIC_DATE_QUERY_OPTIONS,
	TEXT_QUERY_OPTIONS,
	TEXT_TYPES,
} from "../../../../../components/QueryBuilder/constants";

import { IEmployeeField, getEmployeeByID, getGroups } from "../../../../../api/services";

type ValueType =
	| string
	| number
	| { oid: string }
	| { date: string }
	| { relative_date: number; relative_date_unit: string }
	| boolean;

export interface DynamicCohortQueryCondition {
	field: { oid: string } | string;
	value?: ValueType;
	comparator?: string;
}

export interface DynamicCohortQueryOrConditionSet {
	or: Array<DynamicCohortQueryCondition>;
}

export interface DynamicCohortQuery {
	and: Array<DynamicCohortQueryOrConditionSet>;
}

const ALLOWED_FIELDS = ["Manager", "Group"];

export const IS_MANAGER_FIELD = {
	_id: "is_manager",
	name: "Has Direct Reports",
	type: "boolean",
	options: [],
	tags: [],
	"visible-to": [],
} as IEmployeeField;

/**
 * this is where we filter out fields that are not allowed in the dynamic cohort query
 * we currently only allow numeric, manager, and group fields
 */
export const filterEmployeeFieldsForDynamicCohortQuery = (employeeFields: IEmployeeField[] = []): IEmployeeField[] => {
	const filteredFields = employeeFields.filter((field: any) => {
		return (
			NUMERIC_TYPES.includes(field.type) ||
			TEXT_TYPES.includes(field.type) ||
			ALLOWED_FIELDS.includes(field.name) ||
			DATE_TYPES.includes(field.type) ||
			DROPDOWN_SELECTION_TYPES[0] === field.type
		);
	});

	return filteredFields;
};

// formats the entire query for the Dynamic Cohort Query Builder for saving
export const formatQueryForSave = (query: Array<IQuery[]>): DynamicCohortQuery => {
	const formattedQuery: { and: DynamicCohortQueryOrConditionSet[] } = { and: [] };
	query.forEach((orConditionSet) => {
		formattedQuery.and.push(formatOrConditionForSave(orConditionSet));
	});

	return formattedQuery;
};

// formats the or condition set for the Dynamic Cohort Query Builder for saving
export const formatOrConditionForSave = (orConditionSet: IQuery[]): DynamicCohortQueryOrConditionSet => {
	const formattedOrCondition: { or: DynamicCohortQueryCondition[] } = { or: [] };
	orConditionSet.forEach((condition) => {
		const comparator = getComparatorValue(condition);
		const conditionValue = getConditionValue(condition) as ValueType;
		formattedOrCondition.or.push({
			field: condition.field._id === IS_MANAGER_FIELD._id ? IS_MANAGER_FIELD._id : { oid: condition.field._id },
			value: conditionValue,
			comparator,
		});
	});

	return formattedOrCondition;
};

/**
 * calculates/formats the value from the IQuery Condition for saving
 */
export const getConditionValue = (condition: IQuery) => {
	let conditionValue = {};

	if (condition?.field?.type) {
		if (PEOPLE_SELECTION_TYPES.includes(condition.field.type) || condition.field.type === GROUP_TYPE) {
			conditionValue = { oid: condition.queryValue.value };
		} else if (NUMERIC_TYPES.includes(condition.field.type)) {
			conditionValue = Number(condition.queryValue.value);
		} else if (DATE_TYPES.includes(condition.field.type)) {
			conditionValue = calculateDateQueryValue(condition);
		} else {
			conditionValue = getRegexValueIfNeeded(condition.queryValue.value as string, condition.comparator.value);
		}
	}
	return conditionValue;
};

/**
 * calculates the date value from the IQuery Condition for saving as well as determining
 * if the value is for a static or relative date
 */
export const calculateDateQueryValue = (condition: IQuery) => {
	let conditionValue = {};

	if (isValid(condition.queryValue.value)) {
		const dateValue = condition.queryValue.value as Date;

		conditionValue = { date: calculateStaticDateQueryValue(dateValue, condition.comparator.value).toISOString() };
	} else {
		const { length, unit } = condition.queryValue.value as RelativeDateValue;
		conditionValue = { relative_date: Number(length) * -1, relative_date_unit: unit };
	}
	return conditionValue;
};

export const calculateStaticDateQueryValue = (value: Date, comparator: string) => {
	let dateValue = value;
	dateValue.setHours(dateValue.getHours() + dateValue.getTimezoneOffset() / 60);
	if (comparator === "gt") {
		dateValue = endOfDay(dateValue);
	} else {
		dateValue = startOfDay(dateValue);
	}
	dateValue.setHours(dateValue.getHours() - dateValue.getTimezoneOffset() / 60);
	return dateValue;
};

// this function is used to convert search values to regex values if needed
// else it returns the value as is
export const getRegexValueIfNeeded = (value: string, comparator: string) => {
	switch (comparator) {
		case "contains":
		case "notContains": //contains
			break;
		case "startsWith":
		case "notStartsWith": //starts with
			value = `^${value}`;
			break;
		case "endsWith":
		case "notEndsWith": //ends with
			value = `${value}$`;
			break;
	}

	return value;
};

// this function converts comparator value to regex or not_regex for saving
// if value is not a regex comparator, it returns the value as is
export const getComparatorValue = (condition): string => {
	const queryBuilderComparatorValue = condition.comparator.value;
	let comparatorValue = queryBuilderComparatorValue;

	if (REGEX_COMPARATOR_VALUES.includes(queryBuilderComparatorValue)) {
		comparatorValue = "regex";
	} else if (NOT_REGEX_COMPARATOR_VALUES.includes(queryBuilderComparatorValue)) {
		comparatorValue = "not_regex";
	}

	return comparatorValue;
};

/* formats the entire query for the Dynamic Cohort Query Builder for the builder when loading in for editing
 * this is the inverse of formatQueryForSave.
 * Here, we remap the query onto the potential employee field options, then we calculate and get the display values for the fields
 *  and reformat it to a QueryBuilder digesablt format (i.e. IQuery)
 */
export const formatQueryForBuilder = async (
	unformattedQueryForBuilder: DynamicCohortQuery,
	employeeFields: IEmployeeField[]
) => {
	const valuelessQueryMap = unformattedQueryForBuilder.and.map((orConditionSet: DynamicCohortQueryOrConditionSet) => {
		return orConditionSet.or.map((orCondition: DynamicCohortQueryCondition) => {
			return {
				formattedQuery: {
					field: getEmployeeFieldFromOrCondition(orCondition, employeeFields) as IQueryField,
					comparator: getComparatorFromOrCondition(orCondition),
				} as IQuery,
				unformattedQuery: { ...orCondition },
			};
		});
	});

	const formattedQuery = await attachValuesToQuery(valuelessQueryMap);

	if (!validateQuery(formattedQuery)) {
		throw new Error("Invalid Query");
		return [];
	}

	return formattedQuery;
};

/**
 * get employee fields that match on oid and _id unless the field is IS_MANAGER_FIELD, then return without searching for oid
 */
export const getEmployeeFieldFromOrCondition = (
	orCondition: DynamicCohortQueryCondition,
	employeeFields: IEmployeeField[]
) => {
	if (orCondition.field === IS_MANAGER_FIELD._id) {
		return IS_MANAGER_FIELD;
	}

	return employeeFields.find((employeeField: any) => employeeField._id === orCondition.field["oid"]);
};

const getComparatorFromOrCondition = (orCondition: DynamicCohortQueryCondition) => {
	let comparator: { label: string; value: string } | undefined = { label: "", value: "" };

	if (orCondition?.comparator === "regex") {
		const comparatorValue = getRegexComparatorValueFromOrConditionValue(orCondition?.value as string, false);
		comparator = TEXT_QUERY_OPTIONS.find((option: any) => option.value === comparatorValue);
	} else if (orCondition?.comparator === "not_regex") {
		const comparatorValue = getRegexComparatorValueFromOrConditionValue(orCondition?.value as string, true);
		comparator = TEXT_QUERY_OPTIONS.find((option: any) => option.value === comparatorValue);
	} else if (orCondition?.value && orCondition?.value["date"]) {
		const comparatorValue = orCondition?.comparator;
		comparator = STATIC_DATE_QUERY_OPTIONS.find((option: any) => option.value === comparatorValue);
	} else if (orCondition?.value && orCondition?.value["relative_date"]) {
		const comparatorValue = orCondition?.comparator;
		comparator = RELATIVE_DATE_QUERY_OPTIONS.find((option: any) => option.value === comparatorValue);
	} else {
		const masterListOfOptions = NUMERIC_QUERY_OPTIONS.concat(RELATIVE_DATE_QUERY_OPTIONS);
		comparator = masterListOfOptions.find((option: any) => option.value === orCondition.comparator) || {
			label: "",
			value: "",
		};
	}
	if (comparator) {
		return comparator as IComparator;
	}
};

// this function gets the regex comparator value from the or condition value
export const getRegexComparatorValueFromOrConditionValue = (orConditionValue: string, isNotRegex = false) => {
	let comparator = isNotRegex ? "notContains" : "contains";
	if (orConditionValue[0] === "^") {
		comparator = isNotRegex ? "notStartsWith" : "startsWith";
	} else if (orConditionValue[orConditionValue.length - 1] === "$") {
		comparator = isNotRegex ? "notEndsWith" : "endsWith";
	}

	return comparator;
};

const validateQuery = (formattedQuery) => {
	return formattedQuery.every((orConditionSet: any) => {
		return orConditionSet.every((orCondition: any) => {
			return orCondition.field && orCondition.comparator && orCondition.queryValue;
		});
	});
};

// this function attaches the display values and value values to the query for the builder as a Result type
const attachValuesToQuery = async (
	valuelessQueryMap: { formattedQuery: IQuery; unformattedQuery: DynamicCohortQueryCondition }[][]
) => {
	const employeeResults = await getEmployeesForQueryBuilder(valuelessQueryMap);
	const { groups } = await getGroups();
	const groupResults = groups.map((group: any) => ({
		value: group.id,
		label: group.name,
	}));

	return valuelessQueryMap.map(
		(orConditionSet: Array<{ formattedQuery: IQuery; unformattedQuery: DynamicCohortQueryCondition }>) => {
			return orConditionSet.map(
				(orCondition: { formattedQuery: IQuery; unformattedQuery: DynamicCohortQueryCondition }) => {
					const formattedQuery = orCondition.formattedQuery as IQuery;
					const unformattedQuery = orCondition.unformattedQuery;
					const formattedQueryFieldType = formattedQuery.field.type;

					if (formattedQueryFieldType && PEOPLE_SELECTION_TYPES.includes(formattedQueryFieldType)) {
						formattedQuery.queryValue = employeeResults.find(
							(employeeResult) => employeeResult.value === unformattedQuery.value?.["oid"]
						) as QueryValue;
					} else if (formattedQuery.field.type === GROUP_TYPE) {
						formattedQuery.queryValue = groupResults.find(
							(group) => group.value === unformattedQuery.value?.["oid"]
						) as QueryValue;
					} else if (formattedQueryFieldType && DATE_TYPES.includes(formattedQueryFieldType)) {
						if (unformattedQuery.value?.["date"]) {
							const date = new Date(unformattedQuery.value?.["date"]);
							date.setHours(date.getHours() + date.getTimezoneOffset() / 60);
							const formattedLabelDate = format(date, "MMMM d, yyyy");
							formattedQuery.queryValue = { value: date, label: formattedLabelDate } as QueryValue;
						} else if (unformattedQuery.value?.["relative_date"]) {
							const length = unformattedQuery.value?.["relative_date"] * -1;
							const unit = unformattedQuery.value?.["relative_date_unit"];
							const label = getRelativeDateLabel({ length, unit } as RelativeDateValue);
							formattedQuery.queryValue = {
								value: { length, unit } as RelativeDateValue,
								label,
							} as QueryValue;
						}
					} else {
						const nonIdValue = formatNonIdValue(unformattedQuery.value, formattedQuery.comparator.value);
						let label = String(nonIdValue);
						if (nonIdValue === true || nonIdValue === false) {
							label = `${label.charAt(0).toUpperCase() + label.slice(1)}`;
						}
						formattedQuery.queryValue = { value: nonIdValue, label } as QueryValue;
					}

					return formattedQuery;
				}
			);
		}
	);
};

export const formatNonIdValue = (value, comparator: string) => {
	let formattedValue = value;

	if (typeof value === "string") {
		formattedValue = unRegexQueryValue(value, comparator);
	}

	return formattedValue;
};

// gets the employees for the query builder, here we only get the needed employees for the query builder
const getEmployeesForQueryBuilder = async (valuelessQueryMap) => {
	const flatQueryPeopleMap = valuelessQueryMap
		.flat()
		.filter(
			(query: { formattedQuery: IQuery }) =>
				query.formattedQuery.field.type && PEOPLE_SELECTION_TYPES.includes(query.formattedQuery.field.type)
		);
	const employees = await Promise.allSettled(
		flatQueryPeopleMap.map((query: any) => {
			const employeeID = query.unformattedQuery.value.oid;
			return getEmployeeByID(employeeID);
		})
	);

	return employees.map(
		(employee: any) =>
			({
				value: employee?.value?.id,
				label: employee?.value?.name,
			}) as QueryValue
	);
};
